import { ComponentPropsWithoutRef, forwardRef } from "react"

type OysterLogoVariant = "default" | "white" | "black"
type OysterLogoSize = "xs" | "sm" | "md" | "lg" | "full"

interface OysterLogoProps extends ComponentPropsWithoutRef<"svg"> {
  /**
   * Sets the size of the logo. "full" will set the svg to 100%
   * @default "md"
   */
  size?: OysterLogoSize
  /**
   * The accessible name of the logo svg
   * @default "Oyster"
   */
  title?: string
  /**
   * Sets the color of the logo
   * @default "default"
   */
  variant?: OysterLogoVariant
}

const SIZE_TO_WIDTH_VALUE: Record<"xs" | "sm" | "md" | "lg" | "full", number | "100%"> = {
  xs: 64,
  sm: 96,
  md: 192,
  lg: 320,
  full: "100%",
}

export const OysterLogo = forwardRef<SVGSVGElement, OysterLogoProps>(
  ({ size = "md", title = "Oyster", variant = "default", ...rest }, ref) => {
    const fill: Record<OysterLogoVariant, string> = {
      default: "fill-primary-base",
      white: "fill-white",
      black: "fill-black",
    }

    const width = SIZE_TO_WIDTH_VALUE[size]

    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 90 25"
        width={width}
        role="img"
        ref={ref}
        {...rest}
      >
        <title>{title}</title>
        <path
          className={fill[variant]}
          d="m10.86,0C17.03,0,20.56,4.18,20.56,9.55s-3.18,9.6-9.7,9.6S1.15,14.82,1.15,9.55,4.92,0,10.86,0Zm0,16.19c3.45,0,5.58-2.86,5.58-6.63s-2.13-6.66-5.58-6.66-5.58,2.89-5.58,6.66,2.13,6.63,5.58,6.63Zm16.3,2.41c.07.14.04.17-.03.28-.28.42-.84.84-1.68.84-1.08,0-1.75-.56-2.2-.8-.21-.1-.49-.14-.66.07l-1.54,1.68c-.28.25-.28.63,0,.87.98.94,3.07,1.57,4.57,1.47,3.24-.28,4.22-2.06,6-6.04l5.51-12.28c.17-.38-.14-.8-.52-.8h-3.18c-.25,0-.45.17-.56.42l-3.7,9.98c-.07.17-.31.17-.38,0l-3.91-9.98c-.1-.25-.28-.42-.52-.42h-3.21c-.42,0-.7.42-.56.8.07,0,6.46,13.71,6.56,13.92h0Zm17.55.56c3.94,0,6.39-1.92,6.39-4.61,0-5.58-10.26-3.94-10.26-7.05,0-.91.84-1.47,2.27-1.47,1.92,0,3.77.8,4.89,2.34.17.25.49.31.73.17l1.92-1.05c.31-.17.42-.52.21-.8-1.68-2.44-4.71-3.35-7.57-3.35-3.7,0-6.21,1.81-6.21,4.57,0,5.72,9.94,3.8,9.94,6.7,0,.91-.56,1.5-2.13,1.5-1.88,0-3.59-.66-4.78-1.99-.17-.21-.38-.52-.77-.35l-2.72,1.22c-.31.14-.38.49-.14.77,2.65,3.14,5.79,3.39,8.23,3.39h0Zm6.11-15l4.36-3.73c.38-.31.87,0,.87.31v3.07s.04.07.07.07h3.94c.31,0,.59.28.59.59v1.71c0,.32-.28.59-.59.59h-3.94s-.07.04-.07.07v5.65c0,.94.07,3.45,2.13,3.39.63,0,1.26-.17,1.75-.42.14-.07.35-.03.45.14l1.43,2.13c.1.17.07.38-.1.49-1.46.94-3.84,1.08-5.16.84-2.65-.52-4.5-1.95-4.5-5.97v-6.07c0-.38-.1-.63-.35-.91l-1.01-1.19c-.21-.25-.1-.56.14-.77h0Zm17.52-.8c5.06-.04,7.68,3.39,7.78,8.38.03.31-.25.59-.56.59h-10.75c-.1,0-.14.07-.14.14.38,2.58,1.85,3.8,3.94,3.8,1.88,0,3-1.08,3.63-2.13.1-.21.35-.31.59-.25l2.76.8c.28.07.42.38.31.66-1.05,2.3-3.8,3.8-7.29,3.8-5.17,0-8.03-3.18-8.03-8.06s3.45-7.75,7.75-7.75h0Zm-3.56,6.43l7.22-.03s.1,0,.07-.14c-.38-2.16-1.64-3.49-3.73-3.49s-3.35,1.6-3.66,3.53c-.03.14.07.14.1.14h0Zm23.94-3.88c.17.21.21.52-.04.73l-2.09,1.75c-.25.21-.56.17-.77-.07-.31-.42-.91-1.22-1.95-1.22-.8.04-1.57.28-2.2,1.29-.45.66-.59,1.43-.59,2.23v7.54c0,.31-.25.56-.56.56h-2.93c-.31,0-.56-.25-.56-.56V4.46c0-.31.25-.59.56-.59h1.81c.25,0,.45.07.56.45l.39,1.46c.03.07.14.1.21.04.52-.84,1.6-2.02,3.66-2.2,1.95-.14,3.28.73,4.5,2.27h0Z"
        />
      </svg>
    )
  },
)
